import { Link, useLocation, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { SubOrder } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';

/**
 * One order, tracked.
 *
 * A card per sub-order, progressing independently — because they genuinely do.
 * One pharmacy can be out for delivery while another is still packing, and
 * flattening that into a single status bar would be a lie about where the
 * buyer's medicines actually are.
 */
export function OrderScreen() {
  const { reference } = useParams<{ reference: string }>();
  const location = useLocation();
  const justPlaced = (location.state as { justPlaced?: boolean } | null)?.justPlaced ?? false;

  const { data: order, isLoading, isError, refetch } = useQuery({
    queryKey: ['order', reference],
    queryFn: ({ signal }) => api.order(reference!, signal),
    enabled: Boolean(reference),
    // An order in flight changes without the buyer doing anything. Reverb
    // replaces this with a live subscription later in Phase 2.
    refetchInterval: 30_000,
  });

  if (isError) {
    return (
      <>
        <AppBar back title="Order" />
        <ErrorState
          title="We couldn't load this order"
          body="Your order is safe. This is a connection problem, not a payment one."
          action={
            <Button full icon="refresh" onClick={() => refetch()}>
              Try again
            </Button>
          }
        />
      </>
    );
  }

  return (
    <>
      <AppBar
        back
        title={order ? `Order ${order.reference}` : 'Order'}
        subtitle={
          order?.placed_at
            ? `Placed ${new Date(order.placed_at).toLocaleString('en-NG', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}`
            : undefined
        }
      />

      <div className="mx-auto w-full max-w-[900px] px-4 pb-6 xl:px-6">
        {isLoading || !order ? (
          <div className="space-y-3 pt-3.5">
            <Skeleton className="h-24 w-full" radius="card" />
            <Skeleton className="h-64 w-full" radius="card" />
          </div>
        ) : (
          <div className="space-y-3 pt-3.5">
            {justPlaced ? (
              <div className="flex items-start gap-2.5 rounded-card border border-verified/25 bg-verified-tint px-3.5 py-3">
                <Icon name="check" size={16} strokeWidth={3} className="mt-px shrink-0 text-verified" />
                <div>
                  <b className="block text-[13px] text-verified-ink">Order placed</b>
                  <span className="text-[11.5px] leading-relaxed text-verified-ink/80">
                    {order.delivery_count && order.delivery_count > 1
                      ? `${order.delivery_count} pharmacies are preparing your order. Each arrives separately.`
                      : 'The pharmacy has been notified and will start packing shortly.'}
                  </span>
                </div>
              </div>
            ) : null}

            {/* §11 verbatim, when there is a saved card behind it. */}
            {order.card ? (
              <Card className="!py-3">
                <div className="flex items-center gap-2.5">
                  <Icon name="money" size={16} strokeWidth={1.8} className="shrink-0 text-muted" />
                  <div className="min-w-0 flex-1">
                    <b className="block text-[12.5px]">{order.card.label}</b>
                    {order.card.last4 ? (
                      <span className="font-mono text-[11px] text-faint">
                        •••• {order.card.last4}
                      </span>
                    ) : null}
                  </div>
                </div>
              </Card>
            ) : null}

            {order.sub_orders?.map((subOrder) => (
              <SubOrderCard orderReference={order.reference} key={subOrder.reference} subOrder={subOrder} />
            ))}

            <Card>
              <Row label="Medicines" value={order.goods_total_display} />
              <Row label="Delivery" value={order.delivery_total_display} />
              {order.credit_applied_kobo > 0 ? (
                <Row label="Refund credit" value={`−${order.credit_applied_display}`} tone="verified" />
              ) : null}
              <div className="my-3 h-px bg-line-2" />
              <div className="flex items-center justify-between">
                <b className="text-[14px]">Total</b>
                <span className="font-display text-[20px] font-bold tracking-[-0.02em]">
                  {order.grand_total_display}
                </span>
              </div>
            </Card>

            {order.address ? (
              <Card>
                <div className="flex items-start gap-2.5">
                  <Icon name="pin" size={16} strokeWidth={1.9} className="mt-px shrink-0 text-blue" />
                  <div className="min-w-0 text-[12px] leading-relaxed">
                    <b className="block text-[12.5px]">{order.address.recipient_name}</b>
                    <span className="text-muted">{order.address.line}</span>
                    <span className="block font-mono text-[11px] text-faint">
                      {order.address.phone}
                    </span>
                  </div>
                </div>
              </Card>
            ) : null}
          </div>
        )}
      </div>
    </>
  );
}

function SubOrderCard({
  subOrder,
  orderReference,
}: {
  subOrder: SubOrder;
  orderReference: string;
}) {
  return (
    <Card flush>
      <div className="flex items-center gap-2.5 border-b border-line-2 px-3.5 py-3">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-blue-tint font-display text-[11px] font-bold text-blue">
          {subOrder.pharmacy?.initials}
        </span>
        <div className="min-w-0 flex-1">
          <b className="block truncate text-[12.5px]">{subOrder.pharmacy?.name}</b>
          <span className="font-mono text-[10.5px] text-faint">{subOrder.reference}</span>
        </div>
        <Badge tone={statusTone(subOrder.status)}>{subOrder.status_label}</Badge>
      </div>

      {/* §1.3 in the buyer's own words, generated by the policy that runs it. */}
      <div className="border-b border-line-2 bg-blue-tint-2 px-3.5 py-2.5">
        <p className="m-0 text-[11.5px] leading-relaxed text-blue-deep">
          {subOrder.charging.explanation}
        </p>
      </div>

      {/* §3.1: the seal number is on the buyer's screen from dispatch, so they
          can check it against the bag before the rider opens anything. */}
      {subOrder.seal_number ? (
        <div className="flex items-center gap-2.5 border-b border-line-2 px-3.5 py-3">
          {/* §3.1 seal number. A sealed parcel is what this is about — the
              buyer reads it off the screen and matches it against the bag. */}
          <Icon name="box" size={15} strokeWidth={1.9} className="shrink-0 text-verified" />
          <div className="min-w-0 flex-1">
            <span className="block text-[11px] text-faint">Seal number — check it matches the bag</span>
            <b className="font-mono text-[13px] tracking-[0.04em]">{subOrder.seal_number}</b>
          </div>
        </div>
      ) : null}

      <div className="px-3.5 py-3.5">
        <Timeline steps={subOrder.timeline} />
      </div>

      {subOrder.items?.length ? (
        <div className="border-t border-line-2 px-3.5 py-3">
          <p className="m-0 mb-2 text-[10px] font-bold uppercase tracking-[0.08em] text-faint">
            What you ordered
          </p>
          <ul className="m-0 list-none space-y-2 p-0">
            {subOrder.items.map((item, index) => (
              <li key={index} className="flex items-start justify-between gap-3 text-[12px]">
                <span className="min-w-0">
                  <b className="block">
                    {item.name} <span className="font-normal text-faint">× {item.quantity}</span>
                  </b>
                  {/* Checked against the pack at the door. On screen since
                      checkout, so it cannot be produced at handover. */}
                  {item.batch_no ? (
                    <span className="font-mono text-[10.5px] text-faint">
                      Batch {item.batch_no}
                      {item.expiry_date
                        ? ` · exp ${new Date(item.expiry_date).toLocaleDateString('en-NG', {
                            month: 'short',
                            year: 'numeric',
                          })}`
                        : null}
                    </span>
                  ) : null}
                </span>
                <span className="shrink-0 font-mono text-[12px] tabular">
                  {item.line_total_display}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* §4.5 — the door. Offered once there is money behind the delivery and
          nothing already open; which reasons are still available is decided
          by the API on the next screen, where it can also say why. */}
      {subOrder.refund && (subOrder.refund.can_report || subOrder.refund.latest_claim) ? (
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-line-2 px-3.5 py-3">
          {subOrder.refund.latest_claim ? (
            <span className="min-w-0 text-[12px] leading-relaxed text-muted">
              Claim <span className="font-mono">{subOrder.refund.latest_claim.reference}</span> ·{' '}
              <b className="text-ink">{subOrder.refund.latest_claim.status_label}</b>
            </span>
          ) : (
            <span className="min-w-0 text-[12px] text-muted">Something wrong with this delivery?</span>
          )}

          {subOrder.refund.can_report ? (
            <Link
              to={`/orders/${orderReference}/report/${subOrder.reference}`}
              className="inline-flex min-h-11 shrink-0 items-center rounded-btn border border-line bg-white px-3.5 text-[12.5px] font-bold text-ink no-underline hover:border-blue"
            >
              Report a problem
            </Link>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}

function Timeline({ steps }: { steps: SubOrder['timeline'] }) {
  return (
    <ol className="relative m-0 list-none p-0 pl-[26px]">
      <span aria-hidden className="absolute bottom-2.5 left-2 top-1.5 w-0.5 bg-line-2" />

      {steps.map((step) => (
        <li key={step.key} className="relative pb-4 last:pb-0">
          <span
            className={cn(
              'absolute -left-[26px] top-px grid h-[18px] w-[18px] place-items-center rounded-full border-[3px] border-paper',
              step.state === 'done' && 'bg-verified',
              step.state === 'current' && 'bg-blue shadow-[0_0_0_4px_var(--color-blue-tint)]',
              step.state === 'upcoming' && 'bg-line-2',
            )}
          >
            {step.state === 'done' ? (
              <Icon name="check" size={9} strokeWidth={3.5} className="text-white" />
            ) : null}
          </span>

          <b
            className={cn(
              'block text-[12.5px]',
              step.state === 'current' && 'text-blue',
              step.state === 'upcoming' && 'font-normal text-faint',
            )}
          >
            {step.label}
          </b>

          {step.at ? (
            <span className="font-mono text-[10.5px] text-faint">
              {new Date(step.at).toLocaleString('en-NG', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

/**
 * §11: colour is semantic. Green means the thing the buyer wanted has happened,
 * amber means it is waiting on someone, red means it stopped. Nothing here is
 * picked for emphasis.
 */
function statusTone(status: SubOrder['status']): 'verified' | 'caution' | 'danger' | 'brand' {
  const tones: Record<SubOrder['status'], 'verified' | 'caution' | 'danger' | 'brand'> = {
    pending_payment: 'caution',
    pending_review: 'caution',
    paid: 'brand',
    accepted: 'brand',
    packed: 'brand',
    dispatched: 'brand',
    delivered: 'verified',
    completed: 'verified',
    cancelled: 'danger',
    refunded: 'danger',
    partially_refunded: 'caution',
    disputed: 'danger',
  };

  return tones[status] ?? 'brand';
}

function Row({ label, value, tone }: { label: string; value: string; tone?: 'verified' }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <span className="text-[11.5px] text-muted">{label}</span>
      <span
        className={cn('font-mono text-[12.5px] tabular', tone === 'verified' && 'font-bold text-verified')}
      >
        {value}
      </span>
    </div>
  );
}
